import type { HeliInput } from '../sim/heli';

const PREVENT = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Space',
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'KeyE',
  'KeyB',
  'KeyV',
  'Minus',
  'Equal',
]);

/**
 * Keyboard state with two separate sets of key presses. Simulation presses
 * (drop, swap) are only cleared once a simulation tick has actually used them.
 * The simulation runs at 30 Hz while we render at 60, so clearing them once per
 * frame would throw half of them away.
 */
export class Input {
  private readonly down = new Set<string>();
  private readonly simEdge = new Set<string>();
  private readonly uiEdge = new Set<string>();
  /** Set on the first real key or click. Browsers only let sound start after one of those. */
  gestured = false;

  /** Cursor position in logical canvas pixels, or null when off the map. */
  pointer: { x: number; y: number } | null = null;
  /** A click on the map this frame, in logical pixels. */
  click: { x: number; y: number; right: boolean } | null = null;

  /** Track the cursor over the pixel canvas, in the canvas's own coordinates. */
  attachCanvas(canvas: HTMLCanvasElement): void {
    const toLogical = (e: MouseEvent) => {
      const r = canvas.getBoundingClientRect();
      return {
        x: ((e.clientX - r.left) / r.width) * canvas.width,
        y: ((e.clientY - r.top) / r.height) * canvas.height,
      };
    };
    canvas.addEventListener('mousemove', (e) => {
      this.pointer = toLogical(e);
    });
    canvas.addEventListener('mouseleave', () => {
      this.pointer = null;
    });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    canvas.addEventListener('mousedown', (e) => {
      this.gestured = true;
      const p = toLogical(e);
      this.click = { x: p.x, y: p.y, right: e.button === 2 };
    });
  }

  takeClick(): { x: number; y: number; right: boolean } | null {
    const c = this.click;
    this.click = null;
    return c;
  }

  attach(target: Window = window): void {
    target.addEventListener('keydown', (e) => {
      if (PREVENT.has(e.code)) e.preventDefault();
      this.gestured = true;
      if (e.repeat) return;
      this.down.add(e.code);
      this.simEdge.add(e.code);
      this.uiEdge.add(e.code);
    });
    target.addEventListener('keyup', (e) => this.down.delete(e.code));
    target.addEventListener('blur', () => this.down.clear());
    target.addEventListener('pointerdown', () => {
      this.gestured = true;
      // Embedded in a frame on somebody else's page, key presses go to the
      // page around us until this document is focused. A player clicks the
      // game and presses W, nothing moves, and the game looks broken. Taking
      // focus on the first click is what makes the keyboard arrive.
      target.focus();
    });
  }

  held(code: string): boolean {
    return this.down.has(code);
  }

  /** True once per press, for UI keys polled every rendered frame. */
  tapped(code: string): boolean {
    return this.uiEdge.has(code);
  }

  endFrame(): void {
    this.uiEdge.clear();
  }

  /** Build the aircraft input for one simulation tick, and clear the presses it uses. */
  takeHeliInput(): HeliInput {
    const out: HeliInput = {
      up: this.held('KeyW') || this.held('ArrowUp'),
      down: this.held('KeyS') || this.held('ArrowDown'),
      left: this.held('KeyA') || this.held('ArrowLeft'),
      right: this.held('KeyD') || this.held('ArrowRight'),
      interact: this.held('KeyE'),
      drop: this.simEdge.has('Space'),
      swap: this.simEdge.has('KeyB'),
    };
    this.simEdge.clear();
    return out;
  }
}
